import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Chip,
  ListItemIcon
} from '@mui/material';
import VersionDisplay from './VersionDisplay';
import DownloadWindowsDialog from './DownloadWindowsDialog';
import OrganizationSelector from './OrganizationSelector';
import V2Navigation, { drawerWidth as v2DrawerWidth, drawerWidthCollapsed } from './V2Navigation';
import {
  Menu as MenuIcon,
  AdminPanelSettings,
  Logout,
  Person,
  Download,
  Settings
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
  user: any;
  onLogout: () => void;
  selectedOrganizationId?: string;
  onOrganizationChange?: (organizationId: string) => void;
}

export default function Layout({ 
  children, 
  user, 
  onLogout, 
  selectedOrganizationId, 
  onOrganizationChange 
}: LayoutProps) {
  const [v2MobileDrawerOpen, setV2MobileDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [v2MenuCollapsed, setV2MenuCollapsed] = useState<boolean>(false);
  const isPlatformAdmin = user?.systemRole === 'PLATFORM_ADMIN';

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    onLogout();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ width: '100%' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open menu"
            edge="start"
            onClick={() => setV2MobileDrawerOpen(true)}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {/* Logo */}
          <Box
            component="img"
            src="/favicon-32x32.png"
            alt="TimeSentinel"
            sx={{
              height: 32,
              width: 32,
              mr: 2,
            }}
          />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            TimeSentinel Portal
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {onOrganizationChange && (
              <OrganizationSelector
                selectedOrganizationId={selectedOrganizationId}
                onOrganizationChange={onOrganizationChange}
                userRole={user?.systemRole}
              />
            )}
            {isPlatformAdmin && (
              <Chip 
                label="Platform Admin" 
                color="warning" 
                size="small" 
                icon={<AdminPanelSettings />}
              />
            )}
            <Button
              color="inherit"
              onClick={handleProfileMenuOpen}
              startIcon={<Person />}
              endIcon={<Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                {user?.displayName?.charAt(0) || user?.email?.charAt(0)}
              </Avatar>}
            >
              {user?.displayName || user?.email}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <V2Navigation
        user={user}
        selectedOrganizationId={selectedOrganizationId}
        isPlatformAdmin={isPlatformAdmin}
        onMenuCollapsedChange={setV2MenuCollapsed}
        mobileDrawerOpen={v2MobileDrawerOpen}
        onMobileDrawerClose={() => setV2MobileDrawerOpen(false)}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          p: 3,
          mt: 8.125,
          paddingBottom: '60px',
          overflow: 'hidden',
          minWidth: 0,
          width: { 
            xs: '100%',
            sm: `calc(100vw - ${v2MenuCollapsed ? drawerWidthCollapsed : v2DrawerWidth}px)`,
          },
          marginLeft: { xs: 0, sm: `${v2MenuCollapsed ? drawerWidthCollapsed : v2DrawerWidth}px` },
          marginRight: 0,
        }}
      >
        <Box
          data-scroll-container
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Footer with version info */}
      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          left: { xs: 0, sm: v2MenuCollapsed ? '64px' : '240px' },
          py: 1,
          px: 3,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 1000,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          © 2026 TimeSentinel. All rights reserved.
        </Typography>
        <VersionDisplay variant="compact" showLabel={false} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
      >
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          setDownloadDialogOpen(true);
        }}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          Download for Windows
        </MenuItem>
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          if (selectedOrganizationId) {
            navigate(`/org/${selectedOrganizationId}/preferences`);
          } else {
            // For users without organizations, navigate to a general preferences page
            navigate('/preferences');
          }
        }}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Preferences
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <DownloadWindowsDialog open={downloadDialogOpen} onClose={() => setDownloadDialogOpen(false)} />
    </Box>
  );
} 